/**
 * Supabase Client Service
 * Provides authentication and database operations for the frontend
 * Implements Requirements 5.1, 5.2
 */

import { createClient, SupabaseClient, User, Session, AuthError } from '@supabase/supabase-js';
import { config } from '../config/env';
import { UserProfile } from '../types';

// Database types matching backend schema
interface DatabaseUser {
  id: string;
  email: string;
  display_name: string | null;
  avatar: string | null;
  auth_provider: 'google' | 'github' | 'microsoft';
  subscription_tier: 'free' | 'basic' | 'premium';
  created_at: string;
  last_login_at: string;
  usage_count: number;
  settings: Record<string, unknown>;
}

export class SupabaseService {
  private client: SupabaseClient;

  constructor() {
    if (!config.supabase.url || !config.supabase.anonKey) {
      throw new Error(
        'Supabase configuration missing. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY environment variables.'
      );
    }

    this.client = createClient(config.supabase.url, config.supabase.anonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    });
  }

  /**
   * Get the Supabase client instance
   */
  getClient(): SupabaseClient {
    return this.client;
  }

  /**
   * Sign in with OAuth provider
   */
  async signInWithOAuth(
    provider: 'google' | 'github' | 'azure'
  ): Promise<{ error: AuthError | null }> {
    const { error } = await this.client.auth.signInWithOAuth({
      provider: provider,
      options: {
        redirectTo: `${window.location.origin}/`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });

    return { error };
  }

  /**
   * Sign out the current user
   */
  async signOut(): Promise<{ error: AuthError | null }> {
    const { error } = await this.client.auth.signOut();
    return { error };
  }

  /**
   * Get the current session
   */
  async getSession(): Promise<{ session: Session | null; error: AuthError | null }> {
    const { data, error } = await this.client.auth.getSession();
    return { session: data.session, error };
  }

  /**
   * Get the current user
   */
  async getCurrentUser(): Promise<{ user: User | null; error: AuthError | null }> {
    const { data, error } = await this.client.auth.getUser();
    return { user: data.user, error };
  }

  /**
   * Listen to auth state changes
   */
  onAuthStateChange(
    callback: (event: string, session: Session | null) => void
  ): { unsubscribe: () => void } {
    const {
      data: { subscription },
    } = this.client.auth.onAuthStateChange(callback);

    return {
      unsubscribe: () => subscription.unsubscribe(),
    };
  }

  /**
   * Get or create user profile in database
   */
  async getOrCreateUserProfile(authUser: User): Promise<UserProfile> {
    // First, try to get existing user
    const { data: existingUser, error: fetchError } = await this.client
      .from('users')
      .select('*')
      .eq('id', authUser.id)
      .single();

    if (existingUser && !fetchError) {
      // Update last login time
      await this.client
        .from('users')
        .update({ last_login_at: new Date().toISOString() })
        .eq('id', authUser.id);

      return this.mapDatabaseUserToProfile(existingUser);
    }

    // Create new user profile
    const provider = this.getAuthProvider(authUser);
    const newUser: Partial<DatabaseUser> = {
      id: authUser.id,
      email: authUser.email!,
      display_name: authUser.user_metadata?.full_name || authUser.user_metadata?.name || null,
      avatar: authUser.user_metadata?.avatar_url || authUser.user_metadata?.picture || null,
      auth_provider: provider,
      subscription_tier: 'free',
      settings: {
        globalDefaults: {
          context: 'casual',
          genderPreferences: {},
        },
        languagePairDefaults: {},
        contextSpecificDefaults: {},
        preferences: {
          theme: 'auto',
          language: 'en',
          autoSaveHistory: true,
          showQualityScores: true,
          enableAnalytics: true,
          maxHistoryEntries: 100,
        },
        privacy: {
          shareUsageData: true,
          allowTemplateSharing: false,
          dataRetentionDays: 90,
        },
      },
    };

    const { data: createdUser, error: createError } = await this.client
      .from('users')
      .insert(newUser)
      .select()
      .single();

    if (createError) {
      throw new Error(`Failed to create user profile: ${createError.message}`);
    }

    return this.mapDatabaseUserToProfile(createdUser);
  }

  /**
   * Update user profile
   */
  async updateUserProfile(
    userId: string,
    updates: Partial<Pick<DatabaseUser, 'display_name' | 'avatar' | 'settings'>>
  ): Promise<UserProfile> {
    const { data, error } = await this.client
      .from('users')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update user profile: ${error.message}`);
    }

    return this.mapDatabaseUserToProfile(data);
  }

  /**
   * Delete user account
   */
  async deleteUserAccount(userId: string): Promise<void> {
    // Delete user data (cascades to translations and templates via foreign keys)
    const { error: deleteError } = await this.client.from('users').delete().eq('id', userId);

    if (deleteError) {
      throw new Error(`Failed to delete user account: ${deleteError.message}`);
    }

    // Sign out
    await this.signOut();
  }

  /**
   * Map database user to UserProfile type
   */
  private mapDatabaseUserToProfile(dbUser: DatabaseUser): UserProfile {
    // Default settings structure if none exists
    const defaultSettings: UserProfile['settings'] = {
      globalDefaults: {
        context: 'casual',
        genderPreferences: {},
      },
      languagePairDefaults: {},
      contextSpecificDefaults: {},
      preferences: {
        theme: 'auto',
        language: 'en',
        autoSaveHistory: true,
        showQualityScores: true,
        enableAnalytics: true,
        maxHistoryEntries: 100,
      },
      privacy: {
        shareUsageData: true,
        allowTemplateSharing: false,
        dataRetentionDays: 90,
      },
    };

    return {
      id: dbUser.id,
      email: dbUser.email,
      displayName: dbUser.display_name || undefined,
      avatar: dbUser.avatar || undefined,
      subscription: dbUser.subscription_tier,
      settings: (dbUser.settings as unknown as UserProfile['settings']) || defaultSettings,
      createdAt: new Date(dbUser.created_at),
      lastLoginAt: new Date(dbUser.last_login_at),
      usageStats: {
        totalTranslations: dbUser.usage_count,
        thisMonthTranslations: 0, // TODO: Calculate from translations table
        favoriteLanguagePairs: [],
        mostUsedTemplates: [],
      },
    };
  }

  /**
   * Determine auth provider from user metadata
   */
  private getAuthProvider(user: User): 'google' | 'github' | 'microsoft' {
    const provider = user.app_metadata?.provider;
    
    if (provider === 'google') return 'google';
    if (provider === 'github') return 'github';
    if (provider === 'azure') return 'microsoft';
    
    // Default to google if unknown
    return 'google';
  }
}

// Export singleton instance
export const supabaseService = new SupabaseService();
